import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense, lazy } from 'react';
import {
  LayoutDashboard,
  ArrowRightLeft,
  Calculator,
  Edit2,
  X,
  PieChart as PieChartIcon,
  Target,
  Shield,
  Sun,
  Moon,
  Activity,
  LogOut,
  User,
  Languages,
  Eye,
  EyeOff,
  CreditCard,
  Scale
} from 'lucide-react';
import { useCurrentPrices } from './hooks/useCurrentPrices';
import { useAutoDividends } from './hooks/useAutoDividends';
import { useStockLogos } from './hooks/useStockLogos';
import { useTheme } from './hooks/useTheme';
import { useTechnicals } from './hooks/useTechnicals';
import { buildSplitsBySymbol, applySplitAdjustments } from './utils/splitAdjust';
import { initialTransactions } from './constants/investment';
import { migrateTransactions, sortTransactions, generateTxId } from './utils/transactionSync';
import { formatMoney, setGlobalPrivacyMode } from './utils/formatters';
import { calculateHoldings, calculateSummary } from './utils/portfolio';
import { usePlan, PlanProvider } from './contexts/PlanContext';
import PaywallModal from './components/common/PaywallModal';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import LandingPage from './components/LandingPage';
import SupportButton from './components/SupportButton';
import { saveSupportLevels, saveAllocationLimits, saveCostBasisMethod, subscribeToUserData, saveTelegramConfig, trackFeatureUsage, db, subscribeToTransactions, writeTransactionChanges, migrateTransactionsToSubcollection, getTransactionsOnce, getUserData, saveAdminSummary, registerActiveSymbols, refreshPricesNow, setupPresence } from './firebase';
import PullToRefresh from './components/common/PullToRefresh';
import { doc, setDoc } from 'firebase/firestore';
import { logEvent as logAnalyticsEvent, EVENTS as ANALYTICS_EVENTS } from './services/eventLogger';
import { themeClasses } from './theme/classes';
import PwaInstallPrompt from './components/common/PwaInstallPrompt';
import { useCashSummary } from './hooks/useCashSummary';
import { DashboardSkeleton, TransactionsSkeleton, WatchlistSkeleton, AnalyticsSkeleton, SimulatorSkeleton, GenericSkeleton, MarketsSkeleton } from './components/common/Skeletons';
import BottomSheet from './components/common/BottomSheet';
import WelcomePopup from './components/common/WelcomePopup';
import AnnouncementBanner from './components/common/AnnouncementBanner';
import ErrorBoundary from './components/common/ErrorBoundary';

// ★ DashboardTab loaded eagerly (first screen users see)
import DashboardTab from './components/tabs/DashboardTab';
// ★ Lazy-load with retry — handles chunk load failures on slow/unstable mobile networks
const lazyRetry = (importFn) => lazy(() => importFn().catch(() => {
  // If chunk fails to load (network error, cache issue), retry once after short delay
  return new Promise(resolve => setTimeout(resolve, 1500))
    .then(() => importFn())
    .catch(() => importFn()); // final attempt
}));
// Keep import fns referenceable so we can warm (prefetch) chunks on idle —
// this makes the FIRST tap on each bottom-nav tab feel instant.
const importTransactions = () => import('./components/tabs/TransactionsTab');
const importSimulator = () => import('./components/tabs/SimulatorTab');
const importAnalytics = () => import('./components/tabs/AnalyticsTab');
const importWatchlist = () => import('./components/tabs/WatchlistTab');
const importMarkets = () => import('./components/markets/MarketsTab');

const TransactionsTab = lazyRetry(importTransactions);
const SimulatorTab = lazyRetry(importSimulator);
const AnalyticsTab = lazyRetry(importAnalytics);
const WatchlistTab = lazyRetry(importWatchlist);
const MarketsTab = lazyRetry(importMarkets);
const AdminTab = lazyRetry(() => import('./components/admin/AdminTab'));

// Prefetch all main tab chunks during browser idle time (after first paint).
const prefetchTabChunks = () => {
  const run = () => { importWatchlist(); importMarkets(); importTransactions(); importAnalytics(); importSimulator(); };
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else if (typeof window !== 'undefined') {
    setTimeout(run, 2500);
  }
};


// ==========================================
// ส่วนประกอบหลัก (Main App)
// ==========================================

// Dismiss the HTML splash screen once React has mounted
const dismissSplash = () => {
  const el = document.getElementById('app-splash');
  if (el) {
    el.classList.add('hide');
    setTimeout(() => el.remove(), 350);
  }
};

export default function App() {
  const { currentUser, logout, updateUserName, isAdmin } = useAuth();
  const { isPro, isStripeCustomer, _freemiumEnabled } = usePlan();
  const theme = useTheme();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // Dismiss splash as soon as App mounts (React is ready)
  useEffect(() => { dismissSplash(); }, []);

  // Warm up the lazy tab chunks during idle time so the first tap on each
  // bottom-nav tab is instant (no skeleton wait).
  useEffect(() => { prefetchTabChunks(); }, []);

  // Detect standalone mode (user already installed PWA) and record to Firestore
  useEffect(() => {
    if (!currentUser) return;
    const standaloneMedia = window.matchMedia('(display-mode: standalone)').matches;
    const iosStandalone = window.navigator.standalone === true;
    const isStandalone = standaloneMedia || iosStandalone;
    if (!isStandalone) return;
    // Only write once per user — check localStorage to avoid repeated writes
    const key = `pwa_recorded_${currentUser.uid}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    const device = /iphone|ipad|ipod/i.test(navigator.userAgent) ? 'ios' : 'android';
    setDoc(doc(db, 'users', currentUser.uid), {
      pwaInstalled: true,
      pwaInstalledAt: new Date().toISOString(),
      pwaDevice: device
    }, { merge: true }).catch(() => {});
    trackFeatureUsage('pwa_installed');
  }, [currentUser]);

  // Setup Realtime Database Presence
  useEffect(() => {
    if (currentUser) {
      setupPresence(currentUser);
    }
  }, [currentUser]);

  // Detect virtual keyboard to hide bottom nav
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const initialHeight = window.visualViewport.height;
    
    const handleResize = () => {
      // If height shrinks by more than 25%, assume keyboard is open
      if (window.visualViewport.height < initialHeight * 0.75) {
        setIsKeyboardOpen(true);
      } else {
        setIsKeyboardOpen(false);
      }
    };

    window.visualViewport.addEventListener('resize', handleResize);
    return () => window.visualViewport.removeEventListener('resize', handleResize);
  }, []);

  // Pause CSS animations when app is backgrounded to prevent flicker on resume
  useEffect(() => {
    const handleVisibility = () => {
      document.documentElement.style.setProperty(
        '--global-animation-state',
        document.hidden ? 'paused' : 'running'
      );
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const [isPrivacyMode, setIsPrivacyMode] = useState(() => {
    const saved = localStorage.getItem('investport_privacy');
    const initialValue = saved ? JSON.parse(saved) : false;
    setGlobalPrivacyMode(initialValue);
    return initialValue;
  });

  const togglePrivacyMode = () => {
    const newValue = !isPrivacyMode;
    setGlobalPrivacyMode(newValue); // Synchronously update for child components checking !globalPrivacyMode
    setIsPrivacyMode(newValue); // Trigger re-render
    localStorage.setItem('investport_privacy', JSON.stringify(newValue));
  };

  const darkMode = theme.isDark;

  // Paywall popup triggered from WelcomePopup promo
  const [showPaywall, setShowPaywall] = useState(false);
  // PWA install prompt — triggered after WelcomePopup is dismissed
  const [pwaPromptReady, setPwaPromptReady] = useState(false);

  // Admin Impersonation
  const [impersonatedUid, setImpersonatedUid] = useState(null);

  // Landing Page vs Login Page toggle for unauthenticated users
  const [showLoginPage, setShowLoginPage] = useState(() => {
    return window.location.pathname.toLowerCase() === '/login';
  });

  const goToLogin = () => {
    setShowLoginPage(true);
    window.history.pushState({}, '', '/Login');
  };

  const goToLanding = () => {
    setShowLoginPage(false);
    window.history.pushState({}, '', '/');
  };

  // URL Routing Sync
  const validTabs = ['dashboard', 'watchlist', 'markets', 'portfolio', 'analytics', 'tools', 'admin'];
  // Only the FIRST path segment is the main tab; the optional second segment
  // is a sub-tab owned by each tab component (see useSubTabRoute).
  const getMainTabFromPath = () => {
    const seg = window.location.pathname.replace(/^\/+/, '').split('/').filter(Boolean)[0] || '';
    return validTabs.includes(seg.toLowerCase()) ? seg.toLowerCase() : 'dashboard';
  };
  const [activeTab, setTabState] = useState(getMainTabFromPath);
  
  // Detect standalone (installed PWA) mode vs normal browser mode
  const [isStandalone, setIsStandalone] = useState(false);
  useEffect(() => {
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setIsStandalone(!!standalone);
    };
    checkStandalone();
    // Add event listener for changes (e.g. if opened in different context)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', checkStandalone);
      return () => mediaQuery.removeEventListener('change', checkStandalone);
    }
  }, []);

  const setActiveTab = (tab) => {
    if (tab === activeTab) return; // already here — don't clobber the sub-tab in the URL
    // ── Force-clean any leftover body locks (BottomSheet, modals) ──
    // On iOS Safari, switching tabs while a BottomSheet is open can leave
    // body in position:fixed with overflow:hidden → blank screen bug.
    if (document.body.style.position === 'fixed') {
      const scrollY = Math.abs(parseInt(document.body.style.top || '0', 10));
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('bottomsheet-open');
      window.scrollTo(0, scrollY);
    }
    // ── Update UI state + URL FIRST so the tap feels instant ──
    setTabState(tab);
    window.scrollTo({ top: 0, behavior: 'instant' });
    // เปลี่ยน URL ให้เป็นตัวพิมพ์ใหญ่ตัวแรก (เช่น /Transactions) โดยไม่รีโหลดหน้า
    const urlPath = `/${tab.charAt(0).toUpperCase() + tab.slice(1)}`;
    window.history.pushState({}, '', urlPath);
    // ── Defer analytics/tracking to idle time so it never blocks the tab
    //    switch paint (Firestore write + analytics were causing tap lag). ──
    const deferTracking = () => {
      trackFeatureUsage(`tab_${tab}`);
      const evt =
        tab === 'dashboard'   ? ANALYTICS_EVENTS.DASHBOARD_VIEWED :
        tab === 'portfolio'   ? ANALYTICS_EVENTS.TRANSACTIONS_VIEWED :
        tab === 'tools'       ? ANALYTICS_EVENTS.SIMULATOR_OPENED :
        tab === 'analytics'   ? ANALYTICS_EVENTS.ANALYTICS_OPENED :
        tab === 'markets'     ? ANALYTICS_EVENTS.MARKETS_OPENED :
        tab === 'watchlist'   ? ANALYTICS_EVENTS.MARKETS_OPENED : // Watchlist was Markets before
        null;
      if (evt) {
        try { logAnalyticsEvent(evt, { source_screen: 'top_nav' }); } catch {}
      }
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(deferTracking, { timeout: 2000 });
    } else {
      setTimeout(deferTracking, 0);
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      const segs = window.location.pathname.replace(/^\/+/, '').split('/').filter(Boolean);
      const first = (segs[0] || '').toLowerCase();
      // Handle Landing <-> Login for unauthenticated state
      setShowLoginPage(first === 'login');
      // Handle tab routing for authenticated state (sub-tabs handle themselves)
      setTabState(validTabs.includes(first) ? first : 'dashboard');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  const [costBasisMethod, setCostBasisMethod] = useState(() => {
    return localStorage.getItem('investport_cost_basis_method') || 'FIFO';
  });

  const handleCostBasisChange = (method) => {
    setCostBasisMethod(method);
    if (!impersonatedUid) {
      localStorage.setItem('investport_cost_basis_method', method);
      if (currentUser) {
        saveCostBasisMethod(currentUser.uid, method);
      }
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!newProfileName.trim() || newProfileName === currentUser?.displayName) {
      setIsEditingProfile(false);
      return;
    }
    try {
      setIsUpdatingProfile(true);
      if (updateUserName) {
        await updateUserName(newProfileName.trim());
      }
      setIsEditingProfile(false);
    } catch (err) {
      console.error('Failed to update profile', err);
      setIsUpdatingProfile(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('https://us-central1-investport-pro.cloudfunctions.net/createStripePortalSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(language === 'en' ? 'Failed to fetch portal session.' : 'ไม่สามารถเข้าสู่ระบบการตั้งค่าสมาชิกได้');
      }
    } catch (err) {
      console.error('Portal error:', err);
      alert(language === 'en' ? 'Error opening customer portal.' : 'เกิดข้อผิดพลาดในการเปิดระบบสมาชิก');
    }
  };

  // Detect Stripe redirect: ?upgrade=success
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      setShowUpgradeSuccess(true);
      // Clean the URL so refreshing won't re-trigger
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Close user menu when clicking outside
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuspended, setIsSuspended] = useState(false);
  const [currency, setCurrency] = useState(() => localStorage.getItem('investportpro_currency') || 'USD'); // USD or THB
  const [exchangeRate, setExchangeRate] = useState(() => {
    try { const c = localStorage.getItem('investportpro_fx_rate'); return c ? Number(c) : null; } catch { return null; }
  }); // อัตราแลกเปลี่ยนจาก API (cached for instant startup)
  const [language, setLanguage] = useState(() => localStorage.getItem('investportpro_language') || 'th');

  const [transactions, setTransactions] = useState(initialTransactions);

  // Support Levels for Buy-the-Dip Planner
  const [supportLevels, setSupportLevels] = useState(() => {
    try {
      const saved = localStorage.getItem('investportpro_support_levels');
      return saved ? JSON.parse(saved) : {};
    } catch (e) { return {}; }
  });

  const handleSaveSupportLevels = (newLevels) => {
    setSupportLevels(newLevels);
    if (impersonatedUid) return; // Prevent overwriting while impersonating
    localStorage.setItem('investportpro_support_levels', JSON.stringify(newLevels));
    // Also save to Firestore for cross-device sync
    if (currentUser) {
      saveSupportLevels(currentUser.uid, newLevels);
    }
  };

  // Allocation Limits — per-symbol max weight % cap { SYMBOL: maxPercent }
  const [allocationLimits, setAllocationLimits] = useState(() => {
    try {
      const saved = localStorage.getItem('investportpro_allocation_limits');
      return saved ? JSON.parse(saved) : {};
    } catch (e) { return {}; }
  });

  const handleSaveAllocationLimits = (newLimits) => {
    setAllocationLimits(newLimits);
    if (impersonatedUid) return; // Prevent overwriting while impersonating
    localStorage.setItem('investportpro_allocation_limits', JSON.stringify(newLimits));
    if (currentUser) {
      saveAllocationLimits(currentUser.uid, newLimits);
    }
  };

  // Telegram Config State
  const [telegramChatId, setTelegramChatId] = useState(() => {
    try {
      return localStorage.getItem('investportpro_telegram_chat_id') || '';
    } catch (e) { return ''; }
  });

  const handleSaveTelegramConfig = (chatId) => {
    setTelegramChatId(chatId);
    if (impersonatedUid) return;
    localStorage.setItem('investportpro_telegram_chat_id', chatId);
    if (currentUser) {
      saveTelegramConfig(currentUser.uid, chatId);
    }
  };

  // Watchlists State
  const [watchlists, setWatchlists] = useState(() => {
    try {
      const saved = localStorage.getItem('investportpro_watchlists');
      return saved ? JSON.parse(saved) : [{ id: 'default', name: 'My Watchlist', symbols: [] }];
    } catch (e) { return [{ id: 'default', name: 'My Watchlist', symbols: [] }]; }
  });

  const handleSaveWatchlists = (newLists) => {
    setWatchlists(newLists);
    if (impersonatedUid) return;
    localStorage.setItem('investportpro_watchlists', JSON.stringify(newLists));
    if (currentUser) {
      import('./firebase').then(({ saveWatchlists }) => {
        saveWatchlists(currentUser.uid, newLists);
      });
    }
  };

  const { currentPrices, previousCloses, todayCloses, extendedPrices, extendedChanges, highs, lows } = useCurrentPrices(transactions, watchlists);

  // Quick Record from Watchlist
  const [prefillTx, setPrefillTx] = useState(null);
  
  const handleQuickRecordFromWatchlist = (symbol) => {
    setPrefillTx({ symbol, clearPrefill: () => setPrefillTx(null) });
    setActiveTab('portfolio');
  };

  // Open a Tools simulator (Average Cost / Dividend) preselected with a symbol,
  // navigated from the watchlist action menu.
  const [toolsPreselect, setToolsPreselect] = useState(null);
  const handleOpenTool = (view, symbol) => {
    setToolsPreselect({ view, symbol, ts: Date.now() });
    setActiveTab('tools');
  };

  // Deep-link to the Allocation Caps sub-view inside the Markets tab.
  // (Caps moved from Tools to Markets, so the dashboard "over-cap" banner
  //  must land on /Markets/Caps instead of Analytics.)
  const handleNavigateToCaps = () => {
    setActiveTab('markets');
    window.history.replaceState({}, '', '/Markets/Caps');
  };


  // Reference to track sync state
  const isSavingRef = useRef(false);
  const lastSavedRef = useRef(null);
  // ★ Hydration guard: prevents save effect from firing before Firestore data is loaded
  const isHydratedRef = useRef(false);
  // ★ Track whether user has intentionally made changes (vs initial load)
  const userHasMutatedRef = useRef(false);
  // ★ Block saves until Firestore has been reached at least once (prevents stale cache overwrite)
  const hasFreshServerSnapshotRef = useRef(false);
  // ★ Track pending unsaved changes for flush-on-logout
  const pendingSaveRef = useRef(false);
  // ★ Mirror of latest transactions for synchronous diffing inside the committer
  const transactionsRef = useRef(transactions);
  useEffect(() => { transactionsRef.current = transactions; }, [transactions]);
  // ★ Guards the one-shot recovery when the subcollection is unexpectedly empty
  const recoveryAttemptedRef = useRef(false);

  // ★ Granular committer (replaces whole-array save).
  //   1. Optimistically updates local state for an instant UI.
  //   2. Diffs against the previous state by id and writes ONLY the
  //      added/changed/removed transaction docs to the Firestore subcollection.
  //   This eliminates cross-device clobbering — every device's edits merge
  //   document-by-document and propagate in real time, with no "revert".
  const setTransactionsFromUser = useCallback((updater) => {
    const prevState = Array.isArray(transactionsRef.current) ? transactionsRef.current : [];
    const draft = typeof updater === 'function' ? updater(prevState) : updater;
    const arr = Array.isArray(draft) ? draft : [];

    // Ensure every tx has a stable id + updatedAt
    const next = arr.map((t) =>
      t.id
        ? (t.updatedAt ? t : { ...t, updatedAt: new Date().toISOString() })
        : { ...t, id: generateTxId(), updatedAt: t.updatedAt || new Date().toISOString() }
    );
    const nextSorted = sortTransactions(next);

    // Optimistic local update + keep ref in sync synchronously
    transactionsRef.current = nextSorted;
    setTransactions(nextSorted);
    userHasMutatedRef.current = true;

    // Never write to Firestore while impersonating or logged out
    if (!currentUser || impersonatedUid) return;

    // Diff by id → granular upserts/deletes
    const prevMap = new Map(prevState.filter(t => t && t.id).map((t) => [t.id, t]));
    const nextMap = new Map(next.map((t) => [t.id, t]));
    const upserts = [];
    for (const [id, tx] of nextMap) {
      const old = prevMap.get(id);
      if (!old || JSON.stringify(old) !== JSON.stringify(tx)) upserts.push(tx);
    }
    const deletes = [];
    for (const id of prevMap.keys()) if (!nextMap.has(id)) deletes.push(id);

    if (upserts.length || deletes.length) {
      writeTransactionChanges(currentUser.uid, upserts, deletes)
        .catch((e) => console.error('Transaction write failed:', e));
      try { localStorage.setItem('investportpro_transactions_v6', JSON.stringify(nextSorted)); } catch (e) {}
    }
  }, [currentUser, impersonatedUid]);

  // Global Dividend Auto-Scan (uses wrapper since auto-added dividends are real data)
  const { autoAddedCount, setAutoAddedCount, detectedDividends, setDetectedDividends, isScanning, scanProgress, error: scanError, scanForDividends, autoAddedDetails } = useAutoDividends(transactions, setTransactionsFromUser);

  // Temporary self-healing for broken 'STOCK' assetClass from previous quick-record bug
  useEffect(() => {
    if (transactions && transactions.some(t => t.assetClass === 'STOCK')) {
      console.log('Self-healing: fixing broken STOCK assetClasses...');
      setTransactionsFromUser(prev => prev.map(t => t.assetClass === 'STOCK' ? { ...t, assetClass: 'US Stock' } : t));
    }
  }, [transactions, setTransactionsFromUser]);

  const desktopMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (desktopMenuRef.current && !desktopMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load data from Firestore on login + Real-time sync across devices
  useEffect(() => {
    if (!currentUser) {
      // Clear data when logged out
      localStorage.removeItem('investportpro_transactions_v6');
      localStorage.removeItem('investportpro_last_user_id');
      localStorage.removeItem('investportpro_last_synced_at');
      setTransactions(initialTransactions);
      lastSavedRef.current = JSON.stringify(initialTransactions);
      isHydratedRef.current = false;
      userHasMutatedRef.current = false;
      hasFreshServerSnapshotRef.current = false;
      setIsLoading(false);
      return;
    }

    const targetUid = impersonatedUid || currentUser.uid;

    if (!impersonatedUid) {
      // Check if user changed - if so, clear old data first
      const savedUserId = localStorage.getItem('investportpro_last_user_id');
      if (savedUserId && savedUserId !== currentUser.uid) {
        localStorage.removeItem('investportpro_transactions_v6');
        localStorage.removeItem('investportpro_last_synced_at');
        setTransactions(initialTransactions);
        lastSavedRef.current = JSON.stringify(initialTransactions);
      }
      // Save current user id
      localStorage.setItem('investportpro_last_user_id', currentUser.uid);
    }

    let unsubscribe = null;
    let txUnsubscribe = null;
    // Reset all sync guards on each load cycle
    isHydratedRef.current = false;
    userHasMutatedRef.current = false;
    hasFreshServerSnapshotRef.current = false;
    recoveryAttemptedRef.current = false;

    // ★ Real-time TRANSACTIONS handler (subcollection = source of truth).
    //   Fires on every add/edit/delete from ANY device, in real time, and
    //   reflects this device's own optimistic writes via latency compensation.
    const handleTxSnapshot = (txs) => {
      hasFreshServerSnapshotRef.current = true;
      isHydratedRef.current = true;
      setIsLoading(false);

      // ★ SELF-HEALING RECOVERY: if the subcollection is empty but we still have
      //   data in the legacy `transactions` array or the local cache, restore it
      //   instead of showing an empty portfolio. Runs at most once per load.
      if ((!txs || txs.length === 0) && !impersonatedUid && currentUser && !recoveryAttemptedRef.current) {
        recoveryAttemptedRef.current = true;
        (async () => {
          let source = [];
          let ud = null;
          try {
            ud = await getUserData(currentUser.uid);
            if (ud && Array.isArray(ud.transactions)) source = ud.transactions;
          } catch (e) { /* ignore */ }

          // ★ Honor Admin's "Clear Data" action: if data was cleared AFTER our local cache was saved, do not recover.
          const clearedAtStr = ud?.dataClearedAt;
          const localSyncedAtStr = localStorage.getItem('investportpro_last_synced_at');
          if (clearedAtStr) {
            const clearedAt = new Date(clearedAtStr).getTime();
            const localSyncedAt = localSyncedAtStr ? new Date(localSyncedAtStr).getTime() : 0;
            if (clearedAt > localSyncedAt) {
              console.log('🧹 Admin cleared data explicitly. Skipping recovery and wiping local cache.');
              localStorage.removeItem('investportpro_transactions_v6');
              localStorage.removeItem('investportpro_last_synced_at');
              transactionsRef.current = [];
              setTransactions([]);
              return;
            }
          }

          if (source.length === 0) {
            try {
              const cached = JSON.parse(localStorage.getItem('investportpro_transactions_v6') || '[]');
              if (Array.isArray(cached)) source = cached;
            } catch (e) { /* ignore */ }
          }
          if (source.length > 0) {
            const { migrated } = migrateTransactions(source);
            console.log(`🛟 Recovery: restoring ${migrated.length} transactions to subcollection`);
            try {
              await writeTransactionChanges(currentUser.uid, migrated, []);
            } catch (e) {
              console.error('Recovery write failed:', e);
            }
            // The next snapshot will deliver the restored data — keep showing
            // the recovered list in the meantime so the UI never goes blank.
            const sorted = sortTransactions(migrated);
            transactionsRef.current = sorted;
            setTransactions(sorted);
            return;
          }
          // Genuinely empty (new account or all deleted)
          transactionsRef.current = [];
          setTransactions([]);
        })();
        return;
      }

      const { migrated } = migrateTransactions(txs);
      const sorted = sortTransactions(migrated);
      transactionsRef.current = sorted;
      setTransactions(sorted);
      // Only cache a non-empty list so a transient empty snapshot can never wipe
      // the local fast-path cache (which is also a recovery source).
      if (!impersonatedUid && sorted.length > 0) {
        try {
          localStorage.setItem('investportpro_transactions_v6', JSON.stringify(sorted));
          localStorage.setItem('investportpro_last_synced_at', new Date().toISOString());
        } catch (e) {}
      }
    };

    // ★ Real-time listener for the user doc (supportLevels / telegram /
    //   watchlists / suspended). Transactions are handled separately above.
    const handleRealtimeUpdate = (remoteData) => {
      // First snapshot from server → unlock save ability
      if (!hasFreshServerSnapshotRef.current) {
        hasFreshServerSnapshotRef.current = true;
        console.log('🟢 Server connection established via real-time listener');
      }

      if (remoteData && remoteData.supportLevels) {
        setSupportLevels(remoteData.supportLevels);
        localStorage.setItem('investportpro_support_levels', JSON.stringify(remoteData.supportLevels));
      }
      if (remoteData && remoteData.allocationLimits) {
        setAllocationLimits(remoteData.allocationLimits);
        localStorage.setItem('investportpro_allocation_limits', JSON.stringify(remoteData.allocationLimits));
      }
      if (remoteData && remoteData.costBasisMethod) {
        setCostBasisMethod(remoteData.costBasisMethod);
        localStorage.setItem('investport_cost_basis_method', remoteData.costBasisMethod);
      }
      if (remoteData && remoteData.telegramConfig !== undefined) {
        setTelegramChatId(remoteData.telegramConfig);
        localStorage.setItem('investportpro_telegram_chat_id', remoteData.telegramConfig);
      }
      if (remoteData && remoteData.watchlists) {
        setWatchlists(remoteData.watchlists);
        localStorage.setItem('investportpro_watchlists', JSON.stringify(remoteData.watchlists));
      }
      if (remoteData && remoteData.suspended) {
        setIsSuspended(true);
      }
    };

    const loadData = async () => {
      // ★ FAST PATH: Show localStorage cache immediately so the user sees data instantly
      //   while Firestore loads in the background. Only for own account (not impersonating).
      if (!impersonatedUid) {
        try {
          const cachedTx = localStorage.getItem('investportpro_transactions_v6');
          if (cachedTx) {
            const parsed = JSON.parse(cachedTx);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const { migrated } = migrateTransactions(parsed);
              setTransactions(migrated);
              lastSavedRef.current = JSON.stringify(migrated);
              // Also hydrate supportLevels, watchlists and costBasisMethod from cache for a complete fast-path
              try {
                const cachedSL = localStorage.getItem('investportpro_support_levels');
                if (cachedSL) setSupportLevels(JSON.parse(cachedSL));
              } catch (e) {}
              try {
                const cachedWL = localStorage.getItem('investportpro_watchlists');
                if (cachedWL) setWatchlists(JSON.parse(cachedWL));
              } catch (e) {}
              try {
                const cachedTg = localStorage.getItem('investportpro_telegram_chat_id');
                if (cachedTg) setTelegramChatId(cachedTg);
              } catch (e) {}
              try {
                const cachedCB = localStorage.getItem('investport_cost_basis_method');
                if (cachedCB) setCostBasisMethod(cachedCB);
              } catch (e) {}
              // Show the UI immediately with cached data — Firestore will reconcile below
              setIsLoading(false);
              console.log('⚡ Fast-path: showing cached data while Firestore syncs');
            }
          }
        } catch (e) { /* cache read failed, fall through to normal load */ }
      }

      // If fast-path didn't fire (no cache / impersonating), show loading skeleton
      setIsLoading(prev => prev);  // keep current state (already false if fast-path worked)
      if (!lastSavedRef.current) setIsLoading(true);  // only show skeleton if no data at all

      let firebaseModules = null;

      // Step 1: Import Firebase modules
      try {
        firebaseModules = await import('./firebase');
      } catch (importErr) {
        console.error('Failed to import Firebase:', importErr);
      }

      // Step 2: Try to load data from Firestore
      if (firebaseModules) {
        try {
          const data = await firebaseModules.getUserData(targetUid);

          // ★ getUserData succeeded → we know the server state
          hasFreshServerSnapshotRef.current = true;

          // ★ Transactions now live in the subcollection users/{uid}/transactions
          //   (real-time, per-document). The live display is driven by
          //   subscribeToTransactions (Step 3) for the user's own account.
          if (impersonatedUid) {
            // Read-only admin view: prefer the subcollection, fall back to the
            // legacy array for users who haven't migrated yet (admins can't write).
            let txs = [];
            try { txs = await firebaseModules.getTransactionsOnce(targetUid); } catch (e) {}
            if ((!txs || txs.length === 0) && data && Array.isArray(data.transactions)) {
              txs = data.transactions;
            }
            const { migrated } = migrateTransactions(txs);
            setTransactions(sortTransactions(migrated));
          } else if (currentUser && data && Array.isArray(data.transactions) && data.transactions.length > 0) {
            // One-time migration from the legacy array field, if it still exists.
            const { migrated } = migrateTransactions(data.transactions);
            try {
              const res = await firebaseModules.migrateTransactionsToSubcollection(currentUser.uid, migrated);
              if (res && res.migrated) console.log(`🔄 Migrated ${res.count} transactions to subcollection`);
            } catch (mErr) {
              console.error('Transaction migration failed:', mErr);
            }
          }
          isHydratedRef.current = true;

          // Load support levels from Firestore
          if (data && data.supportLevels) {
            setSupportLevels(data.supportLevels);
            if (!impersonatedUid) localStorage.setItem('investportpro_support_levels', JSON.stringify(data.supportLevels));
          } else {
            setSupportLevels({});
          }

          // Load allocation limits from Firestore
          if (data && data.allocationLimits) {
            setAllocationLimits(data.allocationLimits);
            if (!impersonatedUid) localStorage.setItem('investportpro_allocation_limits', JSON.stringify(data.allocationLimits));
          } else {
            setAllocationLimits({});
          }

          // Load cost basis method from Firestore (sync across devices)
          if (data && data.costBasisMethod) {
            setCostBasisMethod(data.costBasisMethod);
            if (!impersonatedUid) localStorage.setItem('investport_cost_basis_method', data.costBasisMethod);
          }

          // Load telegram config
          if (data && data.telegramConfig) {
            setTelegramChatId(data.telegramConfig);
            if (!impersonatedUid) localStorage.setItem('investportpro_telegram_chat_id', data.telegramConfig);
          } else {
            setTelegramChatId('');
          }

          // Load watchlists
          if (data && data.watchlists) {
            setWatchlists(data.watchlists);
            if (!impersonatedUid) localStorage.setItem('investportpro_watchlists', JSON.stringify(data.watchlists));
          } else {
            setWatchlists([{ id: 'default', name: 'My Watchlist', symbols: [] }]);
          }
        } catch (dataErr) {
          console.error('Error loading user data:', dataErr);
          // ★ getUserData failed — load cache for display but hasFreshServerSnapshotRef stays FALSE
          // ★ ONLY use localStorage fallback for own account — never when impersonating
          if (!impersonatedUid) {
            const localDataRaw = localStorage.getItem('investportpro_transactions_v6');
            if (localDataRaw) {
              try {
                const localData = JSON.parse(localDataRaw);
                if (localData && localData.length > 0) {
                  const { migrated } = migrateTransactions(localData);
                  setTransactions(migrated);
                  lastSavedRef.current = JSON.stringify(migrated);
                  console.warn('⚠️ Firestore unreachable, loaded local cache (save BLOCKED until server is reached)');
                }
              } catch (e) { }
            }
          } else {
            // Impersonating and Firestore error → show empty
            setTransactions([]);
            lastSavedRef.current = '[]';
            console.warn('⚠️ Firestore error while impersonating — showing empty portfolio');
          }
          isHydratedRef.current = true; // App is displayable
          // hasFreshServerSnapshotRef stays false → save is blocked
        }

        // Step 3: Real-time listeners for the user's OWN account.
        //   - Transactions subcollection = source of truth (live add/edit/delete).
        //   - User doc (supportLevels/telegram/watchlists/suspended).
        //   Impersonation is a read-only snapshot loaded once above.
        if (!impersonatedUid) {
          txUnsubscribe = firebaseModules.subscribeToTransactions(targetUid, handleTxSnapshot);
          unsubscribe = firebaseModules.subscribeToUserData(targetUid, handleRealtimeUpdate);
        }
      } else {
        // Firebase import itself failed — fully offline
        // ★ Only use localStorage for own account, never when impersonating
        if (!impersonatedUid) {
          const localDataRaw = localStorage.getItem('investportpro_transactions_v6');
          if (localDataRaw) {
            try {
              const localData = JSON.parse(localDataRaw);
              if (localData && localData.length > 0) {
                const { migrated } = migrateTransactions(localData);
                setTransactions(migrated);
                lastSavedRef.current = JSON.stringify(migrated);
              }
            } catch (e) { }
          }
        } else {
          setTransactions([]);
          lastSavedRef.current = '[]';
        }
        isHydratedRef.current = true;
        // hasFreshServerSnapshotRef stays false → save is blocked
        console.warn('⚠️ Fully offline: Firebase unavailable, save is blocked');
      }

      setIsLoading(false);
    };

    loadData();

    // Cleanup: unsubscribe from real-time listeners
    return () => {
      if (unsubscribe) unsubscribe();
      if (txUnsubscribe) txUnsubscribe();
    };
  }, [currentUser, impersonatedUid]);

  // ★ Lightweight cache effect: keep a localStorage copy for the instant fast-path
  //   on the next load. All Firestore writes now happen GRANULARLY inside the
  //   committer (setTransactionsFromUser) against the transactions subcollection,
  //   so there is NO whole-array overwrite here — this is what eliminates the
  //   cross-device "revert" race entirely.
  useEffect(() => {
    if (!currentUser || isLoading || impersonatedUid) return;
    // Never cache an empty list — it would destroy the recovery source during a
    // transient empty snapshot. Clearing all is rare and handled on next load.
    if (!Array.isArray(transactions) || transactions.length === 0) return;
    try {
      localStorage.setItem('investportpro_transactions_v6', JSON.stringify(transactions));
    } catch (e) { /* best-effort cache */ }
  }, [transactions, currentUser, isLoading, impersonatedUid]);

  // ── Split-adjusted transactions ──────────────────────────────────────────
  // Market prices from FMP are split-adjusted, so we reconcile raw user shares
  // to the post-split basis before valuing them (see utils/splitAdjust). This
  // is a non-destructive derived view — the stored/edited transactions (and the
  // Transactions tab) always remain RAW. Split data rides along in the shared
  // technicals doc, so this adds zero extra reads/API calls.
  const { technicals } = useTechnicals();
  const splitsBySymbol = useMemo(() => buildSplitsBySymbol(technicals), [technicals]);
  const adjustedTransactions = useMemo(
    () => applySplitAdjustments(transactions, splitsBySymbol),
    [transactions, splitsBySymbol]
  );

  const holdings = useMemo(() => calculateHoldings(adjustedTransactions, currentPrices, exchangeRate, costBasisMethod), [adjustedTransactions, currentPrices, exchangeRate, costBasisMethod]);

  // คำนวณสรุปเงินสด + ต้นทุนเฉลี่ย USD/THB ก่อน (cash flow is split-invariant → raw)
  const cashSummary = useCashSummary(transactions, exchangeRate);

  const summary = useMemo(() => calculateSummary(holdings, cashSummary?.cashBalanceUSD || 0), [holdings, cashSummary?.cashBalanceUSD]);

  // ซิงค์โลโก้หุ้นกับ Finnhub ใน Background สำหรับหุ้นที่เราไม่มี Logo ออฟไลน์
  const { logos } = useStockLogos(holdings, watchlists);

  // ★ Admin read-model: write a compact portfolio summary to the user doc so the
  //   Admin panel has accurate counts/AUM WITHOUT reading every user's
  //   transactions subcollection. This is a denormalized read model only — the
  //   subcollection remains the single source of truth. We write on structural
  //   changes (tx count / symbols) and refresh AUM at most every 10 minutes.
  const adminSummaryRef = useRef({ fp: null, at: 0 });
  useEffect(() => {
    if (!currentUser || impersonatedUid || isLoading) return;
    if (!isHydratedRef.current || !hasFreshServerSnapshotRef.current) return;

    const nonCash = (transactions || []).filter(t => t.type !== 'DEPOSIT' && t.type !== 'WITHDRAW');
    const tradedSymbols = Array.from(new Set(nonCash.map(t => (t.symbol || '').toUpperCase()).filter(Boolean)));
    const activeHoldings = (holdings || []).filter(h => h.quantity > 0);
    const heldSymbols = Array.from(new Set(activeHoldings.map(h => h.symbol).filter(Boolean)));
    const aumUSD = Math.round((summary?.totalValue || 0) * 100) / 100;

    // Denormalized snapshot for the backend allocation-cap scheduler:
    // { SYMBOL: { q: quantity, c: 'USD'|'THB' } } + fxRate (THB→USD valuation).
    const holdingsSnapshot = {};
    activeHoldings.forEach(h => {
      if (h.symbol) holdingsSnapshot[h.symbol] = { q: h.quantity, c: h.currency || 'USD' };
    });
    const fxRate = exchangeRate || 33;

    const fp = JSON.stringify([(transactions || []).length, nonCash.length, tradedSymbols, holdingsSnapshot]);
    const now = Date.now();
    const structuralChanged = adminSummaryRef.current.fp !== fp;
    const aumStale = now - adminSummaryRef.current.at > 10 * 60 * 1000;
    if (!structuralChanged && !aumStale) return;

    const payload = {
      txCount: (transactions || []).length,
      nonCashCount: nonCash.length,
      tradedSymbols,
      heldSymbols,
      aumUSD,
      holdingsSnapshot,
      fxRate,
      updatedAt: new Date().toISOString(),
    };
    const id = setTimeout(() => {
      saveAdminSummary(currentUser.uid, payload);
      adminSummaryRef.current = { fp, at: Date.now() };
    }, 1500);
    return () => clearTimeout(id);
  }, [transactions, holdings, summary, currentUser, impersonatedUid, isLoading, exchangeRate]);

  // ดึงอัตราแลกเปลี่ยน: ใช้ USD/THB จาก market_prices (FMP) แบบ real-time เป็นหลัก
  // Fallback: Google Sheets → exchangerate-api → ค่าคงที่
  useEffect(() => {
    // ★ ลำดับที่ 1: ใช้ USD/THB จาก market_prices (FMP, อัปเดตทุก 1 นาทีผ่าน central scheduler)
    const tdRate = Number(currentPrices['USD/THB']);
    if (tdRate > 0 && !isNaN(tdRate)) {
      setExchangeRate(tdRate);
      try { localStorage.setItem('investportpro_fx_rate', String(tdRate)); } catch {}
      return; // ใช้ค่าจาก market_prices (FMP) สำเร็จ ไม่ต้อง fetch จากที่อื่น
    }

    // ★ ลำดับที่ 2-3: Fallback สำหรับกรณีที่ market_prices ยังไม่มี USD/THB
    const fetchExchangeRate = async () => {
      // ลองดึงจาก Google Sheets ก่อน
      const EXCHANGE_RATE_CSV_URL = 'https://docs.google.com/spreadsheets/d/1MtqXbhrGC763WLPns9GOACpAq9UXQMZ38gf5IT6lvTs/export?format=csv&gid=1926459604';
      try {
        const response = await fetch(EXCHANGE_RATE_CSV_URL);
        if (response.ok) {
          const csvText = await response.text();
          const lines = csvText.split('\n');
          for (const line of lines) {
            const parts = line.split(',');
            if (parts.length >= 2) {
              const currency = parts[0].replace(/['"]/g, '').trim().toUpperCase();
              const rate = parseFloat(parts[1].replace(/['"]/g, '').trim());
              if ((currency === 'THB' || currency === 'USDTHB') && !Number.isNaN(rate)) {
                setExchangeRate(rate);
                return;
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching from Google Sheets:', error);
      }

      // ถ้าดึงจาก Google Sheets ไม่ได้ ให้ใช้ API เป็น fallback
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        if (data && data.rates && data.rates.THB) {
          setExchangeRate(data.rates.THB);
        }
      } catch (error) {
        console.error('Error fetching exchange rate:', error);
        setExchangeRate(34.50); // fallback rate
      }
    };
    fetchExchangeRate();
  }, [currentPrices]);

  // ฟังก์ชันแปลงเงินตามสกุลเงินที่เลือก
  const formatCurrency = (val, ignorePrivacy = false) => {
    if (isPrivacyMode && !ignorePrivacy) return '******';
    if (currency === 'THB' && exchangeRate) {
      const thbVal = val * exchangeRate;
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(thbVal);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  const formatCurrencyDetail = (val) => {
    if (currency === 'THB' && exchangeRate) {
      const thbVal = val * exchangeRate;
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(thbVal);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(val);
  };

  const toggleCurrency = () => {
    setCurrency(prev => prev === 'USD' ? 'THB' : 'USD');
  };

  const changeCurrency = (nextCurrency) => {
    setCurrency(nextCurrency);
  };

  useEffect(() => {
    localStorage.setItem('investportpro_currency', currency);
  }, [currency]);

  const toggleDarkMode = () => {
    theme.toggle();
  };

  // Pull-to-refresh handler (mobile + PWA). Data is already real-time via
  // Firestore listeners, so this nudges the server to poll fresh prices for the
  // user's active symbols; the live listeners then deliver the updated values.
  const handlePullRefresh = useCallback(async () => {
    try {
      const symbols = [...new Set([
        ...(Array.isArray(transactions) ? transactions.map(t => t.symbol) : []),
        ...((watchlists || []).flatMap(l => l.symbols || [])),
      ].filter(Boolean).map(s => String(s).toUpperCase()))];
      if (symbols.length > 0) {
        // FIRE-AND-FORGET: don't block the spinner on the network round-trip.
        // registerActiveSymbols covers brand-new symbols; refreshPricesNow forces
        // an immediate server poll. The live Firestore listener delivers the fresh
        // prices as soon as the server writes them, so the UI updates on its own —
        // keeping the refresh gesture snappy. Server gate/cooldown caps credits.
        registerActiveSymbols(symbols).catch(() => {});
        refreshPricesNow(symbols).catch(() => {});
      }
    } catch (_e) { /* best-effort */ }
    // Short, fixed delay so the gesture feels responsive — the spinner closes
    // quickly while fresh prices stream in via the live listener.
    await new Promise(r => setTimeout(r, 500));
  }, [transactions, watchlists]);

  const changeLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    localStorage.setItem('investportpro_language', nextLanguage);
    setShowUserMenu(false);
  };

  const uiText = language === 'en'
    ? {
      loading: 'Loading data...',
      syncing: 'Syncing...',
      dashboard: 'Portfolio',
      transactions: 'Transactions',
      simulator: 'Simulator',
      analytics: 'Analytics',
      markets: 'Markets',
      user: 'User',
      logout: 'Log out',
      language: 'Language',
      thai: 'ไทย',
      english: 'English'
    }
    : {
      loading: 'กำลังโหลดข้อมูล...',
      syncing: 'กำลังซิงค์...',
      dashboard: 'พอร์ต',
      transactions: 'ธุรกรรม',
      simulator: 'จำลอง',
      analytics: 'วิเคราะห์',
      markets: 'ตลาด',
      user: 'ผู้ใช้',
      logout: 'ออกจากระบบ',
      language: 'ภาษา',
      thai: 'ไทย',
      english: 'English'
    };

  const handleLogout = async () => {
    try {
      // ★ No pre-logout flush needed: every transaction change is written
      //   immediately and granularly to the subcollection, so there are never
      //   any pending unsaved changes to lose.

      // ★ Clear user-specific caches so the next user on this device does NOT inherit any data.
      // We keep UI preferences (theme, language, currency, privacy) because those are device-level.
      const USER_SCOPED_KEYS = [
        'investportpro_telegram_chat_id',
        'investportpro_support_levels',
        'investportpro_last_dividend_scan',
        'investport_sentiment_cache_v2',
        'investport_surprises_cache_v2',
        'investport_markets_watchlist',
        'investport_stock_logos',
        'investport_stock_logos_v2',
        'portpro_benchmark_cache',
      ];
      USER_SCOPED_KEYS.forEach((key) => {
        try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
      });
      // Force-clear any body scroll lock (from BottomSheet) before navigating
      document.body.style.overflow = '';
      setShowLoginPage(true);
      window.history.replaceState({}, '', '/Login');
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleAppRefresh = () => {
    setShowUserMenu(false);
    setActiveTab('dashboard');
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ถ้ายังไม่ได้ login ให้แสดงหน้า Landing Page หรือ Login Page
  if (!currentUser) {
    // Force-clear body scroll lock in case BottomSheet didn't cleanup
    document.body.style.overflow = '';
    // ถ้า URL ยังค้างอยู่ที่ tab เดิม (เช่น /Dashboard) หลัง logout → เปลี่ยนกลับเป็น /
    const currentPath = window.location.pathname.toLowerCase().replace(/^\/+/, '');
    if (currentPath && currentPath !== 'login' && currentPath !== '') {
      window.history.replaceState({}, '', '/');
    }

    if (showLoginPage || currentPath === 'login') {
      return <>
        <LoginPage language={language} onChangeLanguage={changeLanguage} themeMode={theme.mode} onToggleTheme={toggleDarkMode} onBack={goToLanding} />
        <PwaInstallPrompt language={language} darkMode={theme.isDark} autoDelay={1000} />
      </>;
    }
    return <>
      <LandingPage language={language} onChangeLanguage={changeLanguage} themeMode={theme.mode} onToggleTheme={toggleDarkMode} onGoToLogin={goToLogin} />
      <PwaInstallPrompt language={language} darkMode={theme.isDark} autoDelay={1000} />
    </>;
  }

  // ถ้าล็อคอินแล้วแต่ URL ยังเป็น /Login → เปลี่ยนเป็น /Dashboard
  if (window.location.pathname.toLowerCase() === '/login') {
    window.history.replaceState({}, '', '/Dashboard');
  }

  // Suspended user screen
  if (isSuspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-500/10 flex items-center justify-center">
            <span className="text-3xl">🚫</span>
          </div>
          <h1 className="text-xl font-bold mb-2">บัญชีถูกระงับ</h1>
          <p className="text-slate-400 text-sm mb-6">บัญชีของคุณถูกระงับการใช้งาน หากคิดว่าเกิดข้อผิดพลาด กรุณาติดต่อแอดมิน</p>
          <a href="https://lin.ee/9Mgwhzg" target="_blank" rel="noopener noreferrer" className="inline-block px-5 py-2.5 bg-emerald-600 text-white font-bold text-sm rounded-xl">ติดต่อแอดมิน</a>
          <button onClick={logout} className="block w-full mt-4 text-sm text-slate-500 hover:text-white transition-colors">ออกจากระบบ</button>
        </div>
      </div>
    );
  }

  // Loading state — show skeleton instead of spinner for perceived speed
  if (isLoading) {
    return (
      <div className={themeClasses.appShell}>
        {/* Skeleton header */}
        <header className={`${themeClasses.header} hidden md:block`}>
          <div className="max-w-7xl mx-auto px-6 h-16 flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-xl bg-white/10 animate-pulse" />
              <div className="h-5 w-24 rounded bg-white/10 animate-pulse" />
            </div>
            <div className="flex items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 w-16 rounded-lg bg-white/10 animate-pulse" />
              ))}
            </div>
          </div>
        </header>
        <header className={`${themeClasses.header} md:hidden`}>
          <div className="px-4 h-14 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-white/10 animate-pulse" />
              <div className="h-5 w-20 rounded bg-white/10 animate-pulse" />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/10 animate-pulse" />
              <div className="w-8 h-8 rounded-full bg-white/10 animate-pulse" />
            </div>
          </div>
        </header>
        <main className={themeClasses.mainContent}>
          <DashboardSkeleton />
        </main>
      </div>
    );
  }

  const tabIcons = {
    dashboard: <LayoutDashboard size={20} />,
    watchlist: <Target size={20} />,
    markets: <Activity size={20} />,
    portfolio: <ArrowRightLeft size={20} />,
    analytics: <PieChartIcon size={20} />,
    tools: <Calculator size={20} />,
    ...(isAdmin ? { admin: <Shield size={20} /> } : {})
  };

  const tabLabels = {
    dashboard: uiText.dashboard,
    watchlist: language === 'en' ? 'Watchlist' : 'ติดตาม',
    markets: language === 'en' ? 'Markets' : 'ตลาด',
    portfolio: uiText.transactions,
    analytics: uiText.analytics,
    tools: language === 'en' ? 'Tools' : 'เครื่องมือ',
    ...(isAdmin ? { admin: 'Admin' } : {})
  };

  const mainContent = (
    <div className={themeClasses.appShell}>
      {/* Upgrade Success Modal */}
      {showUpgradeSuccess && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in">
          <div className={`w-full max-w-sm mx-auto rounded-2xl shadow-2xl border overflow-hidden ${theme.isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
            {/* Header */}
            <div className="relative p-8 text-center bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600">
              <div className="absolute inset-0 overflow-hidden">
                {[...Array(20)].map((_, i) => (
                  <div key={i} className="absolute w-1 h-1 rounded-full bg-white/30 animate-ping" style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    animationDuration: `${1 + Math.random() * 2}s`
                  }} />
                ))}
              </div>
              <div className="relative">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <span className="text-3xl">👑</span>
                </div>
                <h2 className="text-2xl font-black text-white">ยินดีต้อนรับสู่ Pro!</h2>
                <p className="text-indigo-100 text-sm mt-2 opacity-90">
                  บัญชีของคุณได้รับการอัปเกรดเรียบร้อยแล้ว
                </p>
              </div>
            </div>
            {/* Body */}
            <div className="p-6">
              <div className="space-y-3 mb-6">
                {[
                  { icon: '♾️', text: 'เพิ่มหลักทรัพย์ได้ไม่จำกัด' },
                  { icon: '🔔', text: 'ตั้งแจ้งเตือน Telegram ไม่อั้น' },
                  { icon: '📊', text: 'เข้าถึงเครื่องมือวิเคราะห์ทั้งหมด' },
                  { icon: '📅', text: 'ปฏิทินปันผล + งบไตรมาสล่วงหน้า' },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${theme.isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                    <span className="text-lg">{item.icon}</span>
                    <span className={`text-sm font-medium ${theme.isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item.text}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setShowUpgradeSuccess(false)}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 transition-all shadow-lg shadow-indigo-600/20"
              >
                เริ่มใช้งาน Pro เลย! 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Header */}
      <header className={`${themeClasses.header} hidden md:block`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex justify-between items-center">
          <button
            type="button"
            onClick={handleAppRefresh}
            className={`${themeClasses.headerBrandButton} space-x-3`}
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Logo"
              className="w-9 h-9 rounded-xl shadow-md border border-white/10"
            />
            <h1 className="text-xl font-bold tracking-tight">Port<span className={themeClasses.headerBrandAccent}>Pro</span><span className="text-xs font-semibold opacity-50 ml-0.5">.app</span></h1>
            {isSyncing && (
              <div className={themeClasses.syncIndicator}>
                <div className={themeClasses.syncDot}></div>
                {uiText.syncing}
              </div>
            )}
          </button>

          <div className="flex items-center gap-4">
            <nav className="flex space-x-1 overflow-x-auto">
              {['dashboard', 'watchlist', 'markets', 'portfolio', 'analytics', 'tools'].map(t => (
                <button key={t} onClick={() => setActiveTab(t)} className={`${themeClasses.desktopNavItem} ${activeTab === t ? themeClasses.desktopNavItemActive : ''}`}>
                  {tabLabels[t]}
                </button>
              ))}
            </nav>

            {/* Privacy Mode Toggle */}
            <button
              onClick={togglePrivacyMode}
              className={`w-9 h-9 ${themeClasses.headerSurfaceButton}`}
              title={language === 'en' ? 'Toggle Privacy Mode' : 'ซ่อนตัวเลขพอร์ต'}
            >
              {isPrivacyMode ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>

            {/* Dark/Light Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`w-9 h-9 ${themeClasses.headerSurfaceButton}`}
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* User Menu */}
            <div className="relative" ref={desktopMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={themeClasses.userMenuTrigger}
              >
                {currentUser.photoURL ? (
                  <img src={currentUser.photoURL} alt="Profile" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className={`w-8 h-8 ${themeClasses.userAvatarFallback}`}>
                    <User size={16} className="text-white" />
                  </div>
                )}
                <span className="text-sm font-medium">
                  {currentUser.displayName || currentUser.email?.split('@')[0]}
                </span>
              </button>

              {showUserMenu && (
                <div className={themeClasses.userMenu}>
                  <div className={`px-4 py-3 ${themeClasses.userMenuSection}`}>
                    {isEditingProfile ? (
                      <form onSubmit={handleUpdateProfile} className="flex flex-col gap-2">
                        <input
                          type="text"
                          value={newProfileName}
                          onChange={(e) => setNewProfileName(e.target.value)}
                          className={`px-2 py-1 text-sm border rounded outline-none focus:ring-1 focus:ring-emerald-500 ${theme.isDark ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-800'}`}
                          placeholder={uiText.user}
                          autoFocus
                          disabled={isUpdatingProfile}
                        />
                        <div className="flex gap-2 justify-end">
                          <button type="button" onClick={() => setIsEditingProfile(false)} disabled={isUpdatingProfile} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                            {language === 'en' ? 'Cancel' : 'ยกเลิก'}
                          </button>
                          <button type="submit" disabled={isUpdatingProfile} className="text-xs text-emerald-600 font-medium hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300">
                            {isUpdatingProfile ? (language === 'en' ? 'Saving...' : 'กำลังบันทึก...') : (language === 'en' ? 'Save' : 'บันทึก')}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="flex justify-between items-start">
                        <div className="overflow-hidden">
                          <p className="text-sm font-semibold theme-text-primary truncate">{currentUser.displayName || uiText.user}</p>
                          <p className="text-xs theme-text-muted truncate">{currentUser.email}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setNewProfileName(currentUser.displayName || '');
                            setIsEditingProfile(true);
                          }}
                          className="ml-2 p-1 text-slate-400 hover:text-emerald-500 rounded transition-colors flex-shrink-0"
                          title={language === 'en' ? 'Edit Profile' : 'แก้ไขโปรไฟล์'}
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className={`px-2 py-2 ${themeClasses.userMenuSection}`}>
                    <div className={themeClasses.userMenuHeading}>
                      <Languages size={14} />
                      {uiText.language}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => changeLanguage('th')}
                        className={`${themeClasses.userMenuChoice} ${language === 'th' ? themeClasses.userMenuChoiceActive : ''}`}
                      >
                        {uiText.thai}
                      </button>
                      <button
                        onClick={() => changeLanguage('en')}
                        className={`${themeClasses.userMenuChoice} ${language === 'en' ? themeClasses.userMenuChoiceActive : ''}`}
                      >
                        {uiText.english}
                      </button>
                    </div>
                  </div>

                  {/* Cost Basis Method */}
                  <div className={`px-2 py-2 ${themeClasses.userMenuSection}`}>
                    <div className={themeClasses.userMenuHeading}>
                      <Scale size={14} />
                      {language === 'en' ? 'Cost Basis Method' : 'วิธีคำนวณต้นทุน'}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleCostBasisChange('FIFO')}
                        className={`${themeClasses.userMenuChoice} ${costBasisMethod === 'FIFO' ? themeClasses.userMenuChoiceActive : ''}`}
                      >
                        FIFO
                      </button>
                      <button
                        onClick={() => handleCostBasisChange('AVG')}
                        className={`${themeClasses.userMenuChoice} ${costBasisMethod === 'AVG' ? themeClasses.userMenuChoiceActive : ''}`}
                      >
                        {language === 'en' ? 'Average' : 'ต้นทุนเฉลี่ย'}
                      </button>
                    </div>
                  </div>

                  {isAdmin && (
                    <div className={`px-2 py-2 ${themeClasses.userMenuSection}`}>
                      <button
                        onClick={() => { setActiveTab('admin'); setShowUserMenu(false); }}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-bold bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-400 dark:hover:bg-rose-900/40 rounded border border-rose-100 dark:border-rose-900/50 transition-colors"
                      >
                        <Shield size={16} />
                        Admin Dashboard
                      </button>
                    </div>
                  )}

                  {isPro && isStripeCustomer && (
                    <div className={`px-2 py-2 ${themeClasses.userMenuSection}`}>
                      <button
                        onClick={() => { handleManageSubscription(); setShowUserMenu(false); }}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-900/60 rounded border border-indigo-100 dark:border-indigo-800 transition-colors"
                      >
                        <CreditCard size={16} />
                        {language === 'en' ? 'Manage Subscription' : 'จัดการสมาชิก'}
                      </button>
                    </div>
                  )}

                  <button
                    onClick={handleLogout}
                    className={themeClasses.userMenuDanger}
                  >
                    <LogOut size={16} />
                    {uiText.logout}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className={`${themeClasses.header} md:hidden`}>
        <div className="px-4 h-14 flex justify-between items-center">
          <button
            type="button"
            onClick={handleAppRefresh}
            className={`${themeClasses.headerBrandButton} space-x-2`}
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Logo"
              className="w-8 h-8 rounded-lg shadow-sm border border-white/10"
            />
            <h1 className="text-lg font-bold tracking-tight">Port<span className={themeClasses.headerBrandAccent}>Pro</span><span className="text-xs font-semibold opacity-50 ml-0.5">.app</span></h1>
            {isSyncing && (
              <div className={themeClasses.syncDot}></div>
            )}
          </button>

          <div className="flex items-center gap-2">
            {/* Privacy Mode Toggle Mobile */}
            <button
              onClick={togglePrivacyMode}
              className={`w-8 h-8 ${themeClasses.headerSurfaceButton}`}
              title={language === 'en' ? 'Toggle Privacy Mode' : 'ซ่อนตัวเลขพอร์ต'}
            >
              {isPrivacyMode ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>

            {/* Dark/Light Mode Toggle Mobile */}
            <button
              onClick={toggleDarkMode}
              className={`w-8 h-8 ${themeClasses.headerSurfaceButton}`}
            >
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {/* User Menu Mobile — opens BottomSheet */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center"
              >
                {currentUser.photoURL ? (
                  <img src={currentUser.photoURL} alt="Profile" className="w-8 h-8 rounded-full theme-accent-border border-2" />
                ) : (
                  <div className={`w-8 h-8 ${themeClasses.userAvatarFallback}`}>
                    <User size={16} className="text-white" />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* In-App Announcement Banner — controlled from Admin Panel */}
      <AnnouncementBanner language={language} darkMode={darkMode} />

      {/* Welcome Popup (Tip of the Day / Promo) — once per session */}
      <WelcomePopup isPro={isPro} language={language} darkMode={darkMode} onUpgrade={() => setShowPaywall(true)} onDismiss={() => setPwaPromptReady(true)} />

      {/* Paywall Modal triggered from promo popup */}
      {showPaywall && <PaywallModal feature="welcome_promo" onClose={() => setShowPaywall(false)} />}

      {/* BottomSheet User Menu (Mobile only) */}
      <BottomSheet isOpen={showUserMenu && typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches} onClose={() => { setShowUserMenu(false); setIsEditingProfile(false); }}>
        <div className="space-y-4">
          {/* Profile section */}
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100 dark:border-slate-700/60">
            {currentUser.photoURL ? (
              <img src={currentUser.photoURL} alt="Profile" className="w-14 h-14 rounded-2xl shadow-sm" />
            ) : (
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${theme.isDark ? 'bg-indigo-900/30' : 'bg-indigo-100'}`}>
                <User size={24} className={theme.isDark ? 'text-indigo-400' : 'text-indigo-600'} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {isEditingProfile ? (
                <form onSubmit={handleUpdateProfile} className="flex flex-col gap-2">
                  <input
                    type="text"
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                    className={`px-3 py-2 text-sm border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 ${theme.isDark ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-800'}`}
                    placeholder={uiText.user}
                    autoFocus
                    disabled={isUpdatingProfile}
                  />
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => setIsEditingProfile(false)} disabled={isUpdatingProfile} className="px-3 py-1 text-xs rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                      {language === 'en' ? 'Cancel' : 'ยกเลิก'}
                    </button>
                    <button type="submit" disabled={isUpdatingProfile} className="px-3 py-1 text-xs rounded-lg font-medium text-white bg-indigo-600">
                      {isUpdatingProfile ? '...' : (language === 'en' ? 'Save' : 'บันทึก')}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <p className="text-base font-bold theme-text-primary truncate">{currentUser.displayName || uiText.user}</p>
                  <p className="text-xs theme-text-muted truncate">{currentUser.email}</p>
                </>
              )}
            </div>
            {!isEditingProfile && (
              <button
                onClick={() => { setNewProfileName(currentUser.displayName || ''); setIsEditingProfile(true); }}
                className="p-2 rounded-xl text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
              >
                <Edit2 size={16} />
              </button>
            )}
          </div>

          {/* Language */}
          <div>
            <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide theme-text-muted">
              <Languages size={14} />
              {uiText.language}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => changeLanguage('th')}
                className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${language === 'th' ? (theme.isDark ? 'bg-indigo-900/40 text-indigo-400 ring-1 ring-indigo-500/30' : 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200') : (theme.isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500')}`}
              >
                🇹🇭 {uiText.thai}
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${language === 'en' ? (theme.isDark ? 'bg-indigo-900/40 text-indigo-400 ring-1 ring-indigo-500/30' : 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200') : (theme.isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500')}`}
              >
                🇺🇸 {uiText.english}
              </button>
            </div>
          </div>

          {/* Cost Basis Method */}
          <div>
            <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide theme-text-muted">
              <Scale size={14} />
              {language === 'en' ? 'Cost Basis Method' : 'วิธีคำนวณต้นทุน'}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleCostBasisChange('FIFO')}
                className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${costBasisMethod === 'FIFO' ? (theme.isDark ? 'bg-indigo-900/40 text-indigo-400 ring-1 ring-indigo-500/30' : 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200') : (theme.isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500')}`}
              >
                FIFO (Dime)
              </button>
              <button
                onClick={() => handleCostBasisChange('AVG')}
                className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${costBasisMethod === 'AVG' ? (theme.isDark ? 'bg-indigo-900/40 text-indigo-400 ring-1 ring-indigo-500/30' : 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200') : (theme.isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500')}`}
              >
                {language === 'en' ? 'Average' : 'ต้นทุนเฉลี่ย'}
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2 pt-2">
            {isAdmin && (
              <button
                onClick={() => { setActiveTab('admin'); setShowUserMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-400 dark:hover:bg-rose-900/40 transition-colors"
              >
                <Shield size={18} />
                Admin Dashboard
              </button>
            )}

            {isPro && isStripeCustomer && (
              <button
                onClick={() => { handleManageSubscription(); setShowUserMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-900/60 transition-colors"
              >
                <CreditCard size={18} />
                {language === 'en' ? 'Manage Subscription' : 'จัดการสมาชิก'}
              </button>
            )}

            <SupportButton currentUser={currentUser} variant="menuItem" />

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut size={18} />
              {uiText.logout}
            </button>
          </div>
        </div>
      </BottomSheet>


      {/* Global Sticky Toast Notification for Auto-Added Dividends */}
      {autoAddedCount > 0 && (
        <div className="fixed top-20 right-4 z-50 animate-in slide-in-from-right-8 fade-in duration-500 max-w-sm w-full shadow-2xl rounded-2xl bg-white border-l-4 border-emerald-500 overflow-hidden">
          <div className="p-4 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-lg flex-shrink-0 animate-bounce">
              🎉
            </div>
            <div className="flex-1 pt-1">
              <h4 className="font-bold text-gray-800">
                {language === 'en'
                  ? `${autoAddedCount} Dividends Auto-Added!`
                  : `เพิ่มออโต้ปันผล ${autoAddedCount} รายการ!`}
              </h4>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                {language === 'en'
                  ? 'We detected new dividends and automatically added them to your portfolio.'
                  : 'เราตรวจพบสิทธิปันผลใหม่และเพิ่มเข้าพอร์ตของคุณเรียบร้อยแล้ว'}
              </p>

              {autoAddedDetails && autoAddedDetails.length > 0 && (
                <div className="mt-2 space-y-1">
                  {autoAddedDetails.slice(0, 3).map((d, i) => (
                    <div key={i} className="text-[10px] font-mono flex justify-between bg-slate-50 px-2 py-1 rounded">
                      <span className="font-bold text-slate-700">{d.symbol} ({d.xdDate})</span>
                      <span className="text-emerald-600">+{formatMoney(d.netAmount)}</span>
                    </div>
                  ))}
                  {autoAddedDetails.length > 3 && (
                    <div className="text-[10px] text-center text-slate-400">
                      {language === 'en' ? `...and ${autoAddedDetails.length - 3} more` : `...และอีก ${autoAddedDetails.length - 3} รายการ`}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setAutoAddedCount(0)}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <div className="bg-emerald-50 px-4 py-2 flex justify-end">
            <button
              onClick={() => { setActiveTab('portfolio'); setAutoAddedCount(0); }}
              className="text-xs font-bold text-emerald-600 hover:text-emerald-700"
            >
              {language === 'en' ? 'View Details →' : 'ดูรายละเอียด →'}
            </button>
          </div>
        </div>
      )}

      {/* Main Content - เพิ่ม pb สำหรับ mobile navigation */}
      <main id="ptr-content" className={themeClasses.mainContent}>
        {impersonatedUid && (
          <div className="bg-rose-500 text-white p-3 text-center rounded-lg mb-4 shadow-md font-bold flex justify-between items-center animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-2">
              <Shield size={18} />
              <span>[ADMIN] Currently viewing UID: {impersonatedUid}</span>
            </div>
            <button
              onClick={() => {
                // Audit the stop action (fire-and-forget).
                try {
                  import('./services/adminActions').then(m =>
                    m.recordAdminAction('impersonate_stop', { targetUid: impersonatedUid })
                  );
                } catch {}
                setImpersonatedUid(null);
                setActiveTab('admin');
              }}
              className="bg-white text-rose-600 px-3 py-1 rounded text-sm hover:bg-rose-50 transition-colors"
            >
              Exit Impersonation
            </button>
          </div>
        )}

        {activeTab === 'dashboard' && <ErrorBoundary darkMode={darkMode} language={language}><DashboardTab summary={summary} holdings={holdings} logos={logos} formatCurrency={formatCurrency} formatCurrencyDetail={formatCurrencyDetail} currency={currency} onToggleCurrency={toggleCurrency} onChangeCurrency={changeCurrency} exchangeRate={exchangeRate} language={language} darkMode={darkMode} supportLevels={supportLevels} allocationLimits={allocationLimits} cashSummary={cashSummary} previousCloses={previousCloses} todayCloses={todayCloses} extendedPrices={extendedPrices} serverTimestamp={currentPrices._lastUpdated} onNavigateToDeposit={() => setActiveTab('portfolio')} onNavigateToAnalytics={() => setActiveTab('analytics')} onNavigateToCaps={handleNavigateToCaps} /></ErrorBoundary>}
        {activeTab === 'watchlist' && <ErrorBoundary darkMode={darkMode} language={language}><Suspense fallback={<WatchlistSkeleton />}><WatchlistTab language={language} darkMode={darkMode} watchlists={watchlists} onSaveWatchlists={handleSaveWatchlists} isPro={isPro} currentPrices={currentPrices} previousCloses={previousCloses} todayCloses={todayCloses} highs={highs} lows={lows} logos={logos} extendedPrices={extendedPrices} extendedChanges={extendedChanges} onAddTransaction={(tx) => setTransactionsFromUser(prev => [...prev, tx])} onQuickRecord={handleQuickRecordFromWatchlist} currentUser={currentUser} holdings={holdings} supportLevels={supportLevels} onSaveSupportLevels={handleSaveSupportLevels} telegramChatId={telegramChatId} onSaveTelegramConfig={handleSaveTelegramConfig} allocationLimits={allocationLimits} onSaveAllocationLimits={handleSaveAllocationLimits} onOpenTool={handleOpenTool} /></Suspense></ErrorBoundary>}
        {activeTab === 'markets' && <ErrorBoundary darkMode={darkMode} language={language}><Suspense fallback={<MarketsSkeleton />}><MarketsTab language={language} darkMode={!!darkMode} holdings={holdings} watchlistSymbols={watchlists?.flatMap(l => l.symbols || []) || []} urlMainTab="markets" /></Suspense></ErrorBoundary>}
        {activeTab === 'portfolio' && <ErrorBoundary darkMode={darkMode} language={language}><Suspense fallback={<TransactionsSkeleton />}><TransactionsTab transactions={transactions} setTransactions={setTransactionsFromUser} exchangeRate={exchangeRate} language={language} darkMode={darkMode} costBasisMethod={costBasisMethod} onCostBasisChange={handleCostBasisChange} detectedDividends={detectedDividends} setDetectedDividends={setDetectedDividends} isScanning={isScanning} scanProgress={scanProgress} scanError={scanError} scanForDividends={scanForDividends} prefillTx={prefillTx} logos={logos} /></Suspense></ErrorBoundary>}
        {activeTab === 'tools' && <ErrorBoundary darkMode={darkMode} language={language}><Suspense fallback={<SimulatorSkeleton />}><SimulatorTab currentUser={currentUser} holdings={holdings} language={language} darkMode={darkMode} supportLevels={supportLevels} onSaveSupportLevels={handleSaveSupportLevels} telegramChatId={telegramChatId} onSaveTelegramConfig={handleSaveTelegramConfig} logos={logos} currentPrices={currentPrices} transactions={transactions} allocationLimits={allocationLimits} onSaveAllocationLimits={handleSaveAllocationLimits} toolsPreselect={toolsPreselect} onToolsPreselectConsumed={() => setToolsPreselect(null)} exchangeRate={exchangeRate} /></Suspense></ErrorBoundary>}
        {activeTab === 'analytics' && <ErrorBoundary darkMode={darkMode} language={language}><Suspense fallback={<AnalyticsSkeleton />}><AnalyticsTab summary={summary} holdings={holdings} logos={logos} transactions={adjustedTransactions} currentPrices={currentPrices} formatCurrency={formatCurrency} currency={currency} onToggleCurrency={toggleCurrency} exchangeRate={exchangeRate} language={language} darkMode={darkMode} cashSummary={cashSummary} allocationLimits={allocationLimits} onSaveAllocationLimits={handleSaveAllocationLimits} /></Suspense></ErrorBoundary>}
        {activeTab === 'admin' && isAdmin && <ErrorBoundary darkMode={darkMode} language={language}><Suspense fallback={<GenericSkeleton />}><AdminTab onImpersonate={(uid) => { setImpersonatedUid(uid); setActiveTab('dashboard'); }} darkMode={darkMode} /></Suspense></ErrorBoundary>}
      </main>

      <SupportButton currentUser={currentUser} />
      <PwaInstallPrompt language={language} darkMode={darkMode} trigger={pwaPromptReady} />

      {/* Mobile Bottom Navigation — animated indicator + scale */}
      {/* data-no-pull-refresh: stop the pull-to-refresh gesture from hijacking
          taps here (it could preventDefault a touch and swallow the click). */}
      {!isKeyboardOpen && (
        <nav data-no-pull-refresh className={`${themeClasses.mobileNav} ${!isStandalone ? '!pb-1' : ''}`}>
          <div className={`flex justify-around items-stretch px-1 relative ${!isStandalone ? 'h-[52px] mb-0' : 'h-[66px] mb-2'}`}>
          {['dashboard', 'watchlist', 'markets', 'portfolio', 'analytics', 'tools'].map(t => {
            const isActive = activeTab === t;
            return (
              <button
                key={t}
                type="button"
                onPointerDown={(e) => {
                  // Prevent default to kill ghost clicks and make it truly instant
                  // but wrap in try-catch just in case it interferes with something
                  try { if (e.cancelable) e.preventDefault(); } catch(err) {}
                  setActiveTab(t);
                  if (navigator.vibrate) navigator.vibrate(10);
                }}
                onClick={() => {
                  // Fallback for non-pointer devices (e.g. older browsers or some desktop cases)
                  setActiveTab(t);
                }}
                aria-label={tabLabels[t]}
                aria-current={isActive ? 'page' : undefined}
                className={`group flex flex-col items-center justify-center flex-1 h-full select-none ${isActive ? 'theme-mobile-nav-item-active' : 'theme-mobile-nav-item'}`}
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              >
                {/* Press feedback lives on the inner wrapper so the button's
                    hit area never shrinks (a scaled button can drop taps near edges). */}
                <span className="flex flex-col items-center justify-center transition-transform duration-150 group-active:scale-90 pointer-events-none">
                  <span className={`rounded-xl transition-transform duration-200 ${!isStandalone ? 'p-1' : 'p-1.5'} ${isActive ? 'theme-mobile-nav-icon-active scale-110 -translate-y-0.5 shadow-sm' : ''}`}>
                    {tabIcons[t]}
                  </span>
                  <span className={`text-[9px] mt-0.5 ${isActive ? 'font-bold opacity-100' : 'font-medium opacity-60'}`}>
                    {tabLabels[t]}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </nav>
      )}
    </div>
  );

  // Wrap the whole app in pull-to-refresh (touch devices only; inert on desktop).
  const refreshableContent = (
    <PullToRefresh onRefresh={handlePullRefresh}>{mainContent}</PullToRefresh>
  );

  // When impersonating, wrap with PlanProvider using the impersonated user's UID
  // so feature locks reflect THEIR plan, not the admin's
  if (impersonatedUid) {
    return <PlanProvider overrideUid={impersonatedUid}>{refreshableContent}</PlanProvider>;
  }
  return refreshableContent;
}
